import { ref, unref } from "vue";
import { httpsCallable } from "firebase/functions";

import { functions } from "@/plugins/firebase";
import { buildCommentAuthorContactRequest } from "@/utils/commentAuthorContact.mjs";

const unrefContext = (context = {}) => ({
  sourceCollectionPath: unref(context.sourceCollectionPath),
  itemId: unref(context.itemId),
  bankId: unref(context.bankId),
  rowState: unref(context.rowState),
});

export const useCommentAuthorContact = (options = {}) => {
  const commentAuthorContactOpen = ref(false);
  const commentAuthorContactLoading = ref(false);
  const commentAuthorContactError = ref("");
  const commentAuthorContact = ref(null);

  const canModerateValue = () => Boolean(unref(options.canModerate));

  const resolveContext = (comment) => {
    const rawContext =
      typeof options.resolveContext === "function"
        ? options.resolveContext(comment) || {}
        : options;
    return unrefContext(rawContext);
  };

  const canOpenCommentAuthorContact = (comment = {}) =>
    Boolean(
      buildCommentAuthorContactRequest(comment, {
        ...resolveContext(comment),
        canModerate: canModerateValue(),
      }),
    );

  const closeCommentAuthorContact = () => {
    commentAuthorContactOpen.value = false;
  };

  const openCommentAuthorContact = async (comment = {}) => {
    const request = buildCommentAuthorContactRequest(comment, {
      ...resolveContext(comment),
      canModerate: canModerateValue(),
    });
    if (!request) return;

    commentAuthorContactOpen.value = true;
    commentAuthorContactLoading.value = true;
    commentAuthorContactError.value = "";
    commentAuthorContact.value = null;

    try {
      const adminGetCommentAuthorContact = httpsCallable(
        functions,
        "adminGetCommentAuthorContact",
      );
      const result = await adminGetCommentAuthorContact(request);
      commentAuthorContact.value = result.data || {};
    } catch (error) {
      console.error("Failed to load commenter contact:", error);
      commentAuthorContactError.value =
        error?.message || "Failed to load commenter contact details.";
    } finally {
      commentAuthorContactLoading.value = false;
    }
  };

  return {
    commentAuthorContactOpen,
    commentAuthorContactLoading,
    commentAuthorContactError,
    commentAuthorContact,
    canOpenCommentAuthorContact,
    closeCommentAuthorContact,
    openCommentAuthorContact,
  };
};
